import React from "react";
import { Check } from "lucide-react";
import { STATE_RING } from "../../lib/state-ring.js";
import { cn } from "../../lib/utils.js";
import type { OverlayPresence } from "../../primitives/OverlaySurface.js";
import type { PositionResult } from "../../primitives/index.js";
import type { ComboboxOption } from "./Combobox.types.js";
import type { ComboboxListboxRows, ListboxLine } from "./combobox-rows.js";

/* The Combobox's portaled listbox, split out of Combobox.tsx so the async rows
   could land without that file growing past its size budget. Internal to the
   molecule: not in the barrel.

   states-check-ignore: the rows here are never focused. Focus stays on the
   combobox input (Combobox.tsx carries its focus treatment) and the active row
   is painted with pb-overlay-paint through aria-activedescendant. */

/* A message line inside the listbox, shown but not exposed: a listbox may own
   only options, so the polite status beside the input is what announces it. */
const messageRow = (line: ListboxLine) => (
  <li
    role="presentation"
    aria-hidden="true"
    className={cn(
      "px-3 py-2 text-sm",
      line.failed ? "text-pb-red-text" : "text-pb-text-muted",
    )}
  >
    {line.text}
  </li>
);

export interface ComboboxListboxProps {
  readonly listRef: React.Ref<HTMLUListElement>;
  readonly id: string;
  readonly optionId: (index: number) => string;
  readonly rows: ComboboxListboxRows;
  /** Async rows speak through `message`; the static empty row stays as it was. */
  readonly remote: boolean;
  readonly activeIndex: number;
  readonly selectedValue: string | undefined;
  readonly emptyMessage: string;
  readonly motionProps: OverlayPresence["motionProps"];
  readonly position: PositionResult | null;
  readonly onActiveIndexChange: (index: number) => void;
  readonly onPick: (option: ComboboxOption) => void;
  readonly onAction: () => void;
}

export function ComboboxListbox({
  listRef,
  id,
  optionId,
  rows,
  remote,
  activeIndex,
  selectedValue,
  emptyMessage,
  motionProps,
  position: pos,
  onActiveIndexChange,
  onPick,
  onAction,
}: ComboboxListboxProps) {
  const { options, message, action, footnote } = rows;
  return (
    <ul
      ref={listRef}
      id={id}
      role="listbox"
      tabIndex={-1}
      aria-busy={rows.busy || undefined}
      {...motionProps}
      style={{
        position: "fixed",
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        minWidth: pos?.width ?? undefined,
        maxWidth: Math.max(pos?.width ?? 0, 320),
        // Apply the engine's viewport-clamped maxHeight (it positions
        // from the content's scrollHeight); a hardcoded max-h would
        // desync rendered height from placement and float the panel.
        maxHeight: pos?.maxHeight,
        overflowY: pos?.maxHeight ? "auto" : undefined,
        visibility: pos ? "visible" : "hidden",
      }}
      className={cn(
        motionProps.className,
        "z-popover py-1",
        "rounded-md border border-pb-control-border bg-pb-card shadow-lg",
      )}
    >
      {message ? messageRow(message) : null}
      {!remote && options.length === 0 ? (
        <li className="px-3 py-2 text-sm text-pb-text-muted">{emptyMessage}</li>
      ) : (
        options.map((o, i) => {
          const selected = o.value === selectedValue;
          const activeOpt = i === activeIndex;
          return (
            <li
              key={o.value}
              id={optionId(i)}
              role="option"
              aria-selected={selected}
              onMouseEnter={() => onActiveIndexChange(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                onPick(o);
              }}
              className={cn(
                "flex items-center justify-between gap-2 px-3 py-1.5 text-sm cursor-pointer transition-colors",
                // The overlay is 0% in high contrast; the ring shows it (d16).
                activeOpt ? `pb-overlay-paint ${STATE_RING.neutral}` : "",
                selected ? "text-pb-teal-text" : "text-pb-text",
              )}
            >
              <span className="wrap-break-word leading-snug">{o.label}</span>
              {selected && <Check className="w-3.5 h-3.5 shrink-0" />}
            </li>
          );
        })
      )}
      {action ? (
        <li
          id={optionId(options.length)}
          role="option"
          aria-selected={false}
          onMouseEnter={() => onActiveIndexChange(options.length)}
          onMouseDown={(e) => {
            e.preventDefault();
            onAction();
          }}
          className={cn(
            "px-3 py-1.5 text-sm font-medium text-pb-text-secondary cursor-pointer transition-colors",
            activeIndex === options.length &&
              `pb-overlay-paint ${STATE_RING.neutral}`,
          )}
        >
          {action.label}
        </li>
      ) : null}
      {footnote ? messageRow(footnote) : null}
    </ul>
  );
}
